import React, { useState } from 'react';
import Box from '../../components/Common/Box';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { useToast } from '../../components/Common/Toast';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import {
    NouveauFractionnement,
    OperationTitres,
    PlanOst,
    useOperationsTitres,
} from '../../hooks/useOperationsTitres';
import SiteLayout from '../../layouts/SiteLayout';

/**
 * Opérations sur titres : enregistrer un fractionnement depuis son avis BRVM, voir ce qu'il
 * changera, suivre son application automatique (premier BOC de la date effective).
 */

const LIBELLES_BILAN: Record<string, string> = {
    portfolios: 'Positions clients',
    ai_positions: 'Positions du trading IA',
    price_alerts: 'Alertes de prix',
    portfolio_tracking: 'Suivis de trailing stop',
    ai_watchlist_stocks: 'Lignes de watchlists IA',
    weekly_predictions: 'Prédictions hebdomadaires',
    stock_notes: 'Notes',
    societes: 'Champs société (titres, flottant)',
    a_revoir: 'Lignes à revoir',
};

const STYLE_CHAMP: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-main)',
    fontSize: '14px',
};

const fmtJour = (iso: string | null) =>
    iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR') : '—';

const fmtFacteur = (f: number | null) => (f == null ? '—' : `×${f.toLocaleString('fr-FR', { maximumFractionDigits: 4 })}`);

const Bilan: React.FC<{ bilan: Record<string, number> }> = ({ bilan }) => (
    <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '13px' }}>
        {Object.entries(bilan).map(([cle, n]) => (
            <li key={cle}>
                {LIBELLES_BILAN[cle] ?? cle} : <strong>{n.toLocaleString('fr-FR')}</strong>
            </li>
        ))}
    </ul>
);

const statutOperation = (op: OperationTitres): { libelle: string; couleur: string } => {
    if (op.application) {
        return op.application.statut === 'A_REVOIR'
            ? { libelle: 'Appliquée — lignes à revoir', couleur: '#b45309' }
            : { libelle: 'Appliquée', couleur: '#03543f' };
    }
    if (op.refus) return { libelle: 'Non applicable', couleur: '#9b1c1c' };
    if (op.en_vigueur) return { libelle: 'En vigueur — application au prochain calcul', couleur: '#1d4ed8' };
    return { libelle: 'Programmée', couleur: '#4b5563' };
};

const FORMULAIRE_VIDE: NouveauFractionnement = {
    societe_symbole: '',
    date_effective: '',
    parite_nouvelles: 10,
    parite_anciennes: 1,
    avis_url: '',
    titre_annonce: '',
};

const AdminOperationsTitresScreen: React.FC = () => {
    const { hasPermission } = useAuth();
    const { operations, loading, error, enregistrer, simuler, appliquer } = useOperationsTitres();
    const { showToast } = useToast();
    const [form, setForm] = useState<NouveauFractionnement>(FORMULAIRE_VIDE);
    const [aConfirmer, setAConfirmer] = useState<'enregistrer' | null>(null);
    const [aAppliquer, setAAppliquer] = useState<OperationTitres | null>(null);
    const [plans, setPlans] = useState<Record<number, PlanOst>>({});
    const [enCours, setEnCours] = useState(false);

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box>
                    <p style={{ padding: '20px' }}>Accès refusé. Permission admin_platform requise.</p>
                </Box>
            </SiteLayout>
        );
    }

    const facteur = form.parite_anciennes > 0 ? form.parite_nouvelles / form.parite_anciennes : 0;
    const formulaireValide =
        form.societe_symbole.trim().length >= 2 &&
        /^\d{4}-\d{2}-\d{2}$/.test(form.date_effective) &&
        form.parite_nouvelles > 0 &&
        form.parite_anciennes > 0 &&
        facteur !== 1 &&
        form.avis_url.trim().startsWith('http');

    const confirmerEnregistrement = async () => {
        setEnCours(true);
        const r = await enregistrer({ ...form, societe_symbole: form.societe_symbole.trim().toUpperCase() });
        setEnCours(false);
        setAConfirmer(null);
        if (r.ok) {
            showToast(
                `Fractionnement ${form.societe_symbole.toUpperCase()} enregistré : il s’appliquera au BOC du ${fmtJour(form.date_effective)}.`,
                'success'
            );
            setForm(FORMULAIRE_VIDE);
        } else {
            showToast(r.erreur ?? 'L’opération n’a pas été enregistrée.', 'error');
        }
    };

    const lancerSimulation = async (op: OperationTitres) => {
        const r = await simuler(op.id);
        if (r.ok && r.data) {
            setPlans((p) => ({ ...p, [op.id]: r.data as PlanOst }));
        } else {
            showToast(r.erreur ?? 'La simulation a échoué.', 'error');
        }
    };

    const confirmerApplication = async () => {
        if (!aAppliquer) return;
        setEnCours(true);
        const r = await appliquer(aAppliquer.id);
        setEnCours(false);
        setAAppliquer(null);
        if (r.ok) {
            showToast(
                r.data && r.data.a_revoir.length
                    ? `Opération appliquée, ${r.data.a_revoir.length} ligne(s) à reprendre à la main (voir le détail).`
                    : 'Opération appliquée.',
                r.data && r.data.a_revoir.length ? 'warning' : 'success'
            );
        } else {
            showToast(r.erreur ?? 'L’application a échoué : rien n’a été modifié.', 'error');
        }
    };

    const champ = (label: string, contenu: React.ReactNode) => (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', minWidth: '140px', flex: '1 1 140px' }}>
            {label}
            {contenu}
        </label>
    );

    return (
        <SiteLayout>
            <Header icon='call_split' title='Opérations sur titres' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <h3 style={{ margin: 0 }}>Enregistrer un fractionnement</h3>
                </div>
                <div className='box-horizontal-padding box-vertical-padding'>
                    <p style={{ margin: '0 0 12px', fontSize: '13px', opacity: 0.85 }}>
                        Saisissez les modalités telles qu’écrites dans l’avis de la BRVM. L’opération s’appliquera d’elle-même à
                        l’import du BOC de sa date effective, avant tout calcul du soir : positions et historique des clients,
                        trading IA, watchlists IA, alertes de prix, suivis, notes, prédictions et nombre de titres.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        {champ('Symbole', (
                            <input style={STYLE_CHAMP} type='text' value={form.societe_symbole} maxLength={10} placeholder='SNTS'
                                onChange={(e) => setForm({ ...form, societe_symbole: e.target.value })} />
                        ))}
                        {champ('Date effective', (
                            <input style={STYLE_CHAMP} type='date' value={form.date_effective}
                                onChange={(e) => setForm({ ...form, date_effective: e.target.value })} />
                        ))}
                        {champ('Actions nouvelles', (
                            <input style={STYLE_CHAMP} type='number' min={1} value={form.parite_nouvelles}
                                onChange={(e) => setForm({ ...form, parite_nouvelles: Number(e.target.value) })} />
                        ))}
                        {champ('pour actions anciennes', (
                            <input style={STYLE_CHAMP} type='number' min={1} value={form.parite_anciennes}
                                onChange={(e) => setForm({ ...form, parite_anciennes: Number(e.target.value) })} />
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '10px' }}>
                        {champ('Lien de l’avis BRVM (fait foi)', (
                            <input style={STYLE_CHAMP} type='url' value={form.avis_url} placeholder='https://www.brvm.org/sites/default/files/…pdf'
                                onChange={(e) => setForm({ ...form, avis_url: e.target.value })} />
                        ))}
                        {champ('Référence de l’avis', (
                            <input style={STYLE_CHAMP} type='text' value={form.titre_annonce ?? ''} placeholder='Avis n°304-2026/BRVM/DG'
                                onChange={(e) => setForm({ ...form, titre_annonce: e.target.value })} />
                        ))}
                        <button type='button' className='button button-purple' disabled={!formulaireValide || enCours}
                            style={{ whiteSpace: 'nowrap', padding: '9px 18px', flexShrink: 0 }}
                            onClick={() => setAConfirmer('enregistrer')}>
                            Enregistrer
                        </button>
                    </div>
                    {facteur > 0 && facteur !== 1 && (
                        <p style={{ margin: '10px 0 0', fontSize: '13px' }}>
                            Facteur : <strong>{fmtFacteur(facteur)}</strong> — chaque action devient {facteur.toLocaleString('fr-FR')} action(s),
                            le cours est divisé d’autant.
                        </p>
                    )}
                </div>
            </Box>

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <h3 style={{ margin: 0 }}>Fractionnements</h3>
                </div>
                <div className='box-horizontal-padding box-vertical-padding'>
                    {loading && <p>Chargement…</p>}
                    {error && <p style={{ color: '#9b1c1c' }}>{error}</p>}
                    {!loading && !error && operations.length === 0 && <p>Aucun fractionnement enregistré depuis 2026.</p>}
                    {operations.map((op) => {
                        const st = statutOperation(op);
                        const plan = plans[op.id];
                        return (
                            <div key={op.id} style={{ borderTop: '1px solid var(--border-color)', padding: '12px 0' }}>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'baseline' }}>
                                    <strong>{op.societe_symbole}</strong>
                                    <span>
                                        {op.parite_nouvelles && op.parite_anciennes
                                            ? `${op.parite_nouvelles} pour ${op.parite_anciennes}`
                                            : fmtFacteur(op.facteur_titres)}{' '}
                                        au {fmtJour(op.date_effective)}
                                    </span>
                                    <span style={{ color: st.couleur, fontWeight: 600 }}>{st.libelle}</span>
                                    {op.avis_url && (
                                        <a href={op.avis_url} target='_blank' rel='noopener noreferrer' style={{ fontSize: '13px' }}>
                                            {op.titre_annonce || 'Avis BRVM'}
                                        </a>
                                    )}
                                </div>
                                {op.refus && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#9b1c1c' }}>{op.refus}</p>}
                                {op.application && (
                                    <div style={{ fontSize: '13px', marginTop: '6px' }}>
                                        Appliquée le {fmtJour(op.application.applied_at)} ({op.application.source === 'AUTO' ? 'automatiquement' : 'par un admin'})
                                        {op.application.bilan && <Bilan bilan={op.application.bilan} />}
                                        {op.application.a_revoir.length > 0 && (
                                            <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: '#b45309' }}>
                                                {op.application.a_revoir.map((l, i) => (
                                                    <li key={i}>
                                                        {LIBELLES_BILAN[l.table] ?? l.table}
                                                        {l.user_id ? ` (utilisateur ${l.user_id})` : ''} : {l.motif}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                {!op.application && !op.refus && (
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                                        <button type='button' className='button button-small' onClick={() => lancerSimulation(op)}>
                                            Simuler
                                        </button>
                                        {op.en_vigueur && (
                                            <button type='button' className='button button-small' disabled={enCours}
                                                onClick={() => setAAppliquer(op)}>
                                                Appliquer maintenant
                                            </button>
                                        )}
                                    </div>
                                )}
                                {plan && (
                                    <div style={{ fontSize: '13px', marginTop: '6px' }}>
                                        Simulation (rien n’est écrit) : titres {fmtFacteur(plan.facteur_titres)}, cours et prix de revient{' '}
                                        {fmtFacteur(plan.facteur_cours).replace('×', '÷')}
                                        <Bilan bilan={plan.bilan} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Box>

            <ConfirmModal
                isOpen={aConfirmer === 'enregistrer'}
                title='Enregistrer ce fractionnement ?'
                message={
                    `${form.societe_symbole.toUpperCase()} : ${form.parite_nouvelles} action(s) nouvelle(s) pour ${form.parite_anciennes} ancienne(s) ` +
                    `au ${fmtJour(form.date_effective)}. À l’import du BOC de ce jour, les positions de tous les détenteurs seront multipliées par ` +
                    `${facteur.toLocaleString('fr-FR')} et leur prix de revient divisé d’autant, une ligne « Fractionnement » sera ajoutée à leur ` +
                    'historique, et les positions du trading IA, watchlists IA, alertes de prix, suivis et nombre de titres seront ajustés. ' +
                    'Vérifiez les modalités sur l’avis : elles ne pourront plus être modifiées une fois appliquées.'
                }
                confirmLabel='Enregistrer'
                cancelLabel='Annuler'
                isDestructive
                isLoading={enCours}
                onConfirm={confirmerEnregistrement}
                onCancel={() => setAConfirmer(null)}
            />
            <ConfirmModal
                isOpen={aAppliquer !== null}
                title='Appliquer le fractionnement maintenant ?'
                message={
                    aAppliquer
                        ? `Modifie immédiatement les positions de tous les détenteurs de ${aAppliquer.societe_symbole} ` +
                          `(quantités ×${aAppliquer.facteur_titres?.toLocaleString('fr-FR')}, prix de revient divisés d’autant), ` +
                          'ainsi que le trading IA, les watchlists IA, les alertes et le nombre de titres. Normalement inutile : ' +
                          'l’application est automatique au calcul du soir. Opération non réversible depuis cet écran.'
                        : ''
                }
                confirmLabel='Appliquer'
                cancelLabel='Annuler'
                isDestructive
                isLoading={enCours}
                onConfirm={confirmerApplication}
                onCancel={() => setAAppliquer(null)}
            />
        </SiteLayout>
    );
};

export default AdminOperationsTitresScreen;
